import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { concatenateName, DateFormatter, TimeFormatter } from "@/utils/functions/helperFunctions";
import { TemplateTableSkeletonDesktop, TemplateTableSkeletonMobile } from "@/components/skeletons";
import { useCompactListLayout } from "@/utils/hooks/useCompactListLayout";
import { NO_SEARCH_RESULTS, TABLE_HEADERS, LABELS, GLOBAL_ORGANIZATION } from "@/utils/constants/appConstants/commonConstants";
import NoDataAvailable from "@/components/cards/NoDataAvailable";
import { MobileDateField } from "@/components/cards/MobileDateField";
import { MobileListChevron } from "@/components/cards/MobileListChevron";
import TransferOwnershipModal from "@/components/modals/TransferOwnershipModal";
import TransferOwnershipButton from "@/components/buttons/TransferOwnershipButton";
import { transferTemplateOwnership } from "@/services/template.service";
import { isFeaturePermitted } from "@/utils/functions/rbac";
import { getCurrentOrganization } from "@/utils/functions/OrganizationFunctions";
import NoFilterDataCard from "@/components/cards/NoFilterDataCard";

interface TemplateTableProps {
    templates: any;
    loadingData: boolean;
    setTemplates: any;
    dataLength: number;
    sorting: string;
    isLoading: boolean;
    allFetched?: boolean;
    setSorting: ({ }) => void;
    search: (searchText: string) => void;
    reset: () => void;
    searchItem: string | null;
    title: string;
    dataLoad: boolean;
    resetDataQueries: () => void;
    setPageNo: (pageNo: number) => void;
    id: string;
}

function TemplateTable(props: TemplateTableProps) {
    const navigate = useNavigate();
    const isCompactLayout = useCompactListLayout();
    const [subjectStatus, setSubjectStatus] = useState(true);
    const [updatedAtStatus, setUpdatedAtStatus] = useState(true);
    const [sortElement, setSortElement] = React.useState("created_at");

    // Transfer ownership states
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
    const [transferLoading, setTransferLoading] = useState(false);
    const [isTransferTemplateOwnershipPermitted, setIsTransferTemplateOwnershipPermitted] = useState(false);

    const lastChangeSort = () => {
        setUpdatedAtStatus(!updatedAtStatus);
        setSortElement("updated_at");
        props.setPageNo(1);
        props.setSorting({ updated_at: !updatedAtStatus ? 1 : -1 });
    };

    const currentOrganization = getCurrentOrganization();
    const showActionsColumn = isTransferTemplateOwnershipPermitted && (currentOrganization?.name !== GLOBAL_ORGANIZATION);
    const tableColSpan = showActionsColumn ? 8 : 7;
    // Check permissions on component mount
    React.useEffect(() => {
        setIsTransferTemplateOwnershipPermitted(isFeaturePermitted('AdminPortalTransferTemplateOwnership'));
    }, []);

    // Handle opening transfer ownership modal
    const handleOpenTransferModal = (template: any) => {
        setSelectedTemplate(template);
        setShowTransferModal(true);
    };

    // Handle transfer ownership
    const handleTransferOwnership = async (selectedUser: any) => {
        if (!selectedUser || !selectedTemplate) return;

        setTransferLoading(true);
        try {
            // Prepare payload - use user_id if available, otherwise use email_address
            const payload: { user_id?: string; email_address?: string } = {};

            if (selectedUser.user?.id) {
                payload.user_id = selectedUser.user.id;
            } else if (selectedUser.user?.email_address) {
                payload.email_address = selectedUser.user.email_address;
            } else {
                throw new Error('No valid user identifier found');
            }

            // Call the API
            await transferTemplateOwnership(selectedTemplate.id, payload);

            // Close modal and show success message
            setShowTransferModal(false);
            setSelectedTemplate(null);
            props.setPageNo(1);
            props.resetDataQueries();
            // You can add a success toast/notification here
            console.log('Template ownership transferred successfully to:', selectedUser.user?.first_name, selectedUser.user?.last_name);

        } catch (error) {
            console.error('Error transferring template ownership:', error);
            // You can add an error toast/notification here
        } finally {
            setTransferLoading(false);
        }
    };

    return (
        <>
            {isCompactLayout ? (
                props.templates && !props.dataLoad ? (
                    props.templates?.length > 0 ? (
                        <div className="envelope_table">
                            {props.templates?.map((template: any, key: number) => (
                                <div className="envelope_tr gs-mobile-list-card" key={template.id}>
                                    <div className="gs-mobile-card-icon">
                                        <em className="icon ni ni-template icons" id={`${props.id}-icon-${key}-mb`}></em>
                                    </div>
                                    <div className="env_meta_data gs-mobile-list-card__content w-100" id={`${props.id}-subject-${key}-mb`}>
                                        {template.name}
                                        <div className="text-dark">
                                            <span className="fw-bold">Sent by: </span>
                                            <span className="tb-lead-sub" id={`${props.id}-owner-${key}-mb`}>
                                                {concatenateName(template.owner?.first_name, template.owner?.last_name)}
                                            </span>
                                        </div>
                                        <div className="text-dark">
                                            <span className="fw-bold">Envelope Count: </span>
                                            <span id={`${props.id}-envelope-count-${key}-mb`}>{template.envelope_count ?? 0}</span>
                                            <span className="fw-bold ms-2">Deleted: </span>
                                            <span id={`${props.id}-deleted-envelope-count-${key}-mb`}>{template.deleted_envelope_count ?? 0}</span>
                                            <span className="fw-bold ms-2">Total: </span>
                                            <span id={`${props.id}-total-envelope-count-${key}-mb`}>{template.total_envelope_count ?? 0}</span>
                                        </div>
                                        <MobileDateField
                                            label="Created At: "
                                            value={template.created_at}
                                            dateId={`${props.id}-created-at-date-${key}-mb`}
                                            timeId={`${props.id}-created-at-time-${key}-mb`}
                                        />
                                        <MobileDateField
                                            label="Updated At: "
                                            value={template.updated_at}
                                            dateId={`${props.id}-updated-at-date-${key}-mb`}
                                            timeId={`${props.id}-updated-at-time-${key}-mb`}
                                        />
                                        {showActionsColumn ? (
                                            <div className="gs-mobile-transfer-ownership">
                                                <TransferOwnershipButton
                                                    id={`template-transfer-ownership-btn-${key}-mb`}
                                                    iconId={`template-transfer-ownership-icon-${key}-mb`}
                                                    onClick={() => handleOpenTransferModal(template)}
                                                />
                                            </div>
                                        ) : null}
                                    </div>
                                    <MobileListChevron
                                        id={`${props.id}-redirect-${key}-mb`}
                                        onClick={() => navigate(`/template-details/${template.id}`)}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : props.searchItem ? (
                        <NoDataAvailable>
                            <p id={`${props.id}-no-search-result`}>{NO_SEARCH_RESULTS}</p>
                        </NoDataAvailable>
                    ) : (
                        <NoDataAvailable>
                            <NoFilterDataCard id={props.id} loadAll={props.resetDataQueries} />
                        </NoDataAvailable>
                    )
                ) : (
                    <TemplateTableSkeletonMobile showActionColumn={showActionsColumn} />
                )
            ) : (
                <table
                    className={`nk-tb-list nk-tb-ulist table-weight gs-template-table${showActionsColumn ? " gs-template-table--with-actions" : ""}`}
                    style={{ tableLayout: "fixed", width: "100%" }}
                >
                    <colgroup>
                        <col className="gs-template-col-name" />
                        <col className="gs-template-col-envelope-count" />
                        <col className="gs-template-col-deleted-count" />
                        <col className="gs-template-col-total-count" />
                        <col className="gs-template-col-owner" />
                        <col className="gs-template-col-created" />
                        <col className="gs-template-col-updated" />
                        {showActionsColumn ? <col className="gs-template-col-actions" /> : null}
                    </colgroup>
                    <thead>
                        <tr className="nk-tb-item nk-tb-head">
                            <th className="nk-tb-col gs-template-col-name">
                                <span id={`${props.id}-name-th`}>{TABLE_HEADERS.NAME}</span>
                            </th>
                            <th className="nk-tb-col gs-template-col-envelope-count">
                                <span id={`${props.id}-envelope-count-th`}>{TABLE_HEADERS.ENVELOPE_COUNT}</span>
                            </th>
                            <th className="nk-tb-col gs-template-col-deleted-count">
                                <span id={`${props.id}-deleted-envelope-count-th`}>{TABLE_HEADERS.DELETED_ENVELOPE_COUNT}</span>
                            </th>
                            <th className="nk-tb-col gs-template-col-total-count">
                                <span id={`${props.id}-total-envelope-count-th`}>{TABLE_HEADERS.TOTAL_ENVELOPE_COUNT}</span>
                            </th>
                            <th className="nk-tb-col gs-template-col-owner">
                                <span id={`${props.id}-owner-th`}>{TABLE_HEADERS.OWNER}</span>
                            </th>
                            <th className="nk-tb-col gs-template-col-created">
                                <span id={`${props.id}-created-at-th`}>{TABLE_HEADERS.CREATED_AT}</span>
                            </th>
                            <th className="nk-tb-col gs-template-col-updated" onClick={lastChangeSort}>
                                <span id={`${props.id}-updated-at-th`}>{TABLE_HEADERS.LAST_CHANGED}</span>
                            </th>
                            {showActionsColumn && (
                                <th className="nk-tb-col gs-template-col-actions">
                                    <span id={`${props.id}-actions-th`}>{TABLE_HEADERS.ACTIONS}</span>
                                </th>
                            )}
                        </tr>
                    </thead>
                    {props.templates && !props.dataLoad ? (
                        props.templates?.length > 0 ? (
                            <tbody>
                                {props.templates.map((template: any, key: number) => (
                                    <tr className="nk-tb-item" key={key}>
                                        <td className="nk-tb-col gs-template-col-name subject-col">
                                            <Link to={"/template-details/" + template?.id} className="project-title">
                                                <div className="icon mr-2 fs-5 mt-1">
                                                    <em className="icon ni ni-template text-gray"></em>
                                                </div>
                                                <div className="data" style={{ marginTop: 4, maxWidth: "80%", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                                                    <div className="info">
                                                        <div className="text-dark name d-md-none">{template.name}</div>
                                                        <div className="text-dark name tb-col-md">{template.name}</div>
                                                    </div>
                                                </div>
                                            </Link>
                                        </td>
                                        <td className="nk-tb-col gs-template-col-envelope-count tb-col-mb text-left" style={{ maxWidth: "100px" }}>
                                            <div className="meta">
                                                <Link to={`/template/${template.id}/envelopes`} className="link link-primary" id={`${props.id}-envelope-count-${key}`}>
                                                    {template.envelope_count ?? 0}
                                                </Link>
                                            </div>
                                        </td>
                                        <td className="nk-tb-col gs-template-col-deleted-count tb-col-mb text-left" style={{ maxWidth: "100px" }}>
                                            <div className="meta">
                                                <span className="text-dark" id={`${props.id}-deleted-envelope-count-${key}`}>
                                                    {template.deleted_envelope_count ?? 0}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="nk-tb-col gs-template-col-total-count tb-col-mb text-left" style={{ maxWidth: "100px" }}>
                                            <div className="meta">
                                                <span className="text-dark" id={`${props.id}-total-envelope-count-${key}`}>
                                                    {template.total_envelope_count ?? 0}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="nk-tb-col gs-template-col-owner tb-col-mb text-left" style={{ maxWidth: "100px" }}>
                                            <div className="meta">
                                                <span className="text-dark">
                                                    {concatenateName(template.owner?.first_name, template.owner?.last_name)}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="nk-tb-col gs-template-col-created tb-col-mb" style={{ maxWidth: "100px" }}>
                                            <span className="text-dark">
                                                <span>{DateFormatter(template.created_at)}</span>
                                            </span>
                                            <div>
                                                <div className="meta mt-1">
                                                    <span className="version">
                                                        <span className="text-soft">{TimeFormatter(template?.created_at)}</span>
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="nk-tb-col gs-template-col-updated tb-col-mb" style={{ maxWidth: "100px" }}>
                                            <span className="text-dark">
                                                <span>{DateFormatter(template.updated_at)}</span>
                                            </span>
                                            <div>
                                                <div className="meta mt-1">
                                                    <span className="version">
                                                        <span className="text-soft">{TimeFormatter(template?.updated_at)}</span>
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                {isTransferTemplateOwnershipPermitted && (currentOrganization?.name !== GLOBAL_ORGANIZATION) && (
                                    <td className="nk-tb-col gs-template-col-actions">
                                        <TransferOwnershipButton
                                            id={`template-transfer-ownership-btn-${key}`}
                                            iconId={`template-transfer-ownership-icon-${key}`}
                                            onClick={() => handleOpenTransferModal(template)}
                                        />
                                    </td>
                                )}
                                    </tr>
                                ))}
                        {props.isLoading && <TemplateTableSkeletonDesktop rowCount={2} showActionColumn={isTransferTemplateOwnershipPermitted && (currentOrganization?.name !== GLOBAL_ORGANIZATION)} />}
                    </tbody>
                ) : props.searchItem ? (
                    <tbody>
                        <tr>
                            <td colSpan={tableColSpan}>
                                <NoDataAvailable>
                                    <p id={`${props.id}-no-search-result`}>{NO_SEARCH_RESULTS}</p>
                                </NoDataAvailable>
                            </td>
                        </tr>
                    </tbody>
                ) : (
                    <tbody>
                        <tr>
                            <td colSpan={tableColSpan}>
                                <NoDataAvailable>
                                    <NoFilterDataCard id={props.id} loadAll={props.resetDataQueries} />
                                </NoDataAvailable>
                            </td>
                        </tr>
                    </tbody>
                )
            ) : (
                <tbody>
                    <TemplateTableSkeletonDesktop showActionColumn={isTransferTemplateOwnershipPermitted && (currentOrganization?.name !== GLOBAL_ORGANIZATION)} />
                </tbody>
            )}
                </table>
            )}

            {/* Transfer Ownership Modal */}
            {selectedTemplate && (
                <TransferOwnershipModal
                    show={showTransferModal}
                    onHide={() => {
                        setShowTransferModal(false);
                        setSelectedTemplate(null);
                    }}
                    onTransferOwnership={handleTransferOwnership}
                    loading={transferLoading}
                    currentOwnerId={selectedTemplate?.owner?.id}
                    title={LABELS.TRANSFER_TEMPLATE_OWNERSHIP}
                    description={LABELS.TRANSFER_OWNERSHIP_DESCRIPTION}
                />
            )}
        </>
    );
}

export default TemplateTable;
